import React, { useState } from 'react'
import { Button, Dialog, Flex, Select, Text, TextArea } from 'components/ds'
export type FeedbackDialogOption = {
  label: string
  value: string
  freeText?: boolean
}
export type FeedbackDialogSelection = {
  value: string
  freeText: string
}

export type FeedbackDialogProps = React.ComponentProps<
  typeof Dialog.Content
> & {
  title: string
  description: string
  options?: FeedbackDialogOption[]
  optionLabel: string
  optionPrompt: string
  freeTextLabel: string
  confirmButtonLabel: string
  cancelButtonLabel: string
  onConfirm: (selection?: FeedbackDialogSelection) => void
  onCancel?: () => void
  initialOption?: string
}

const FeedbackDialog = ({
  title,
  description,
  options = [],
  optionLabel,
  optionPrompt,
  freeTextLabel,
  confirmButtonLabel,
  cancelButtonLabel,
  onConfirm,
  onCancel,
  initialOption,
  ...rest
}: FeedbackDialogProps): React.ReactNode => {
  const [option, setOption] = useState<FeedbackDialogOption | null | undefined>(
    options.find(o => o.value === initialOption),
  )
  const [freeText, setFreeText] = useState('')

  const handleOptionChange = (value?: string) => {
    const optionFound = options.find(o => o.value === value)
    setOption(optionFound)
    if (!optionFound?.freeText) setFreeText('')
  }

  const handleConfirm = () => {
    onConfirm(
      option
        ? {
            value: option.value,
            freeText,
          }
        : undefined,
    )
    setFreeText('')
  }

  return (
    <Dialog.Content
      maxWidth="400px"
      onOpenAutoFocus={() => handleOptionChange(initialOption)}
      {...rest}
    >
      <Dialog.Title mt="0">{title}</Dialog.Title>
      <Dialog.Description size="2" mb="4">
        {description}
      </Dialog.Description>

      <Flex direction="column" gap="3">
        {options.length > 1 && (
          <label>
            <Text as="div" size="2" mb="1">
              {optionLabel}
            </Text>
            <Select.Root
              value={option?.value}
              onValueChange={handleOptionChange}
            >
              <Flex width="100%" asChild>
                <Select.Trigger placeholder={optionPrompt} />
              </Flex>
              <Select.Content>
                {options.map(option => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </label>
        )}
        {(option?.freeText || options.length === 0) && (
          <label>
            <Text as="div" size="2" mb="1">
              {freeTextLabel}
            </Text>
            <TextArea
              rows={6}
              value={freeText}
              onChange={e => setFreeText(e.target.value)}
            />
          </label>
        )}
      </Flex>

      <Flex gap="3" mt="4" justify="end">
        <Dialog.Close>
          <Button variant="soft" color="gray" onClick={onCancel}>
            {cancelButtonLabel}
          </Button>
        </Dialog.Close>
        <Dialog.Close>
          <Button color="red" onClick={handleConfirm}>
            {confirmButtonLabel}
          </Button>
        </Dialog.Close>
      </Flex>
    </Dialog.Content>
  )
}

export default FeedbackDialog
